/**
 * Videoteca Técnica Canônica da Honda ADV 150 (Projeto K0W / Bloco KF43E)
 * Padrão Concessionária Honda Dream & Filosofia Fábio Akita (Zero Mocks & Fail-Visible)
 * 16 Procedimentos Homologados com YouTube IDs Verificados via oEmbed (Status 200)
 */

import type { CatalogSubsystemId } from "./catalog.js";

export interface VideoTimestamp {
  time: string; // Formato "MM:SS"
  seconds: number;
  step: string;
  detail: string;
}

export interface VideoTorqueReference {
  name: string;
  nm: number;
  kgfm: number;
  socket: string;
  notes?: string;
}

export interface TechnicalVideoItem {
  id: string;
  title: string;
  channel: string;
  youtubeVideoId: string; // 11 caracteres alfanuméricos verificados via oEmbed
  url: string;
  mirrorUrl?: string;
  subsystem: CatalogSubsystemId;
  subsystemLabel: string;
  description: string;
  difficulty: "Iniciante" | "Intermediário" | "Avançado" | "Crítico";
  estimatedDurationMin: number;
  relatedPartNumbers: string[]; // Vinculação com códigos OEM em catalog.ts
  torqueSpecs: VideoTorqueReference[];
  specialTools: string[];
  criticalGotcha: string;
  timestamps: VideoTimestamp[];
}

export const ADV150_TECHNICAL_VIDEOS: ReadonlyArray<TechnicalVideoItem> = [
  {
    id: "v_oleo_motor",
    title: "Troca do Óleo do Motor eSP & Limpeza da Tela de Filtragem M30",
    channel: "Mitch's Scooter Stuff",
    youtubeVideoId: "kzsmcnUJ2qg",
    url: "https://www.youtube.com/watch?v=kzsmcnUJ2qg",
    mirrorUrl: "https://www.youtube.com/watch?v=kzsmcnUJ2qg",
    subsystem: "motor",
    subsystemLabel: "Motor eSP 149,3 cm³",
    description:
      "Drenagem do cárter pelo bujão M12 (24 N.m), remoção da tampa sextavada M30 (20 N.m), lavagem da tela cilíndrica com O-ring novo e abastecimento de 800 ml de óleo Pro Honda 10W-30 JASO MB.",
    difficulty: "Iniciante",
    estimatedDurationMin: 8,
    relatedPartNumbers: [
      "90131-883-000",
      "94109-12000",
      "15421-KPL-900",
      "15426-KVB-900",
      "12361-035-000",
      "91302-001-020",
      "08232-M99-K1JT1",
    ],
    torqueSpecs: [
      {
        name: "Bujão de Dreno M12 do Cárter",
        nm: 24,
        kgfm: 2.4,
        socket: "Sextavado 12 mm",
        notes: "Substituir arruela de alumínio 12 mm (94109-12000)",
      },
      {
        name: "Tampa da Tela de Óleo M30",
        nm: 20,
        kgfm: 2.0,
        socket: "Sextavado 17 mm",
        notes: "Lubrificar anel O-ring (91302-001-020) com óleo novo",
      },
    ],
    specialTools: [
      "Soquete 12 mm",
      "Soquete 17 mm",
      "Torquímetro de estalo 10-60 N.m",
      "Proveta graduada 1.000 ml",
      "Funil com bico flexível",
    ],
    criticalGotcha:
      "MANDATÓRIO: Utilizar óleo com especificação JASO MB. O uso de óleo JASO MA (com modificadores para embreagem úmida) eleva o atrito interno e degrada o consumo. Nunca ultrapassar 24 N.m no cárter de alumínio.",
    timestamps: [
      {
        time: "00:00",
        seconds: 0,
        step: "Ferramental & Insumos",
        detail: "Soquetes 12 e 17 mm, torquímetro, Pro Honda 10W-30 MB, proveta e arruela 12 mm.",
      },
      {
        time: "00:45",
        seconds: 45,
        step: "Aquecimento & Nivelamento",
        detail: "Motor 3-5 min em lenta; scooter no cavalete central sobre piso plano.",
      },
      {
        time: "01:40",
        seconds: 100,
        step: "Remoção do Bujão M12",
        detail: "Esgotamento do cárter inferior pelo parafuso sextavado de 12 mm.",
      },
      {
        time: "02:30",
        seconds: 150,
        step: "Esgotamento Residual",
        detail: "Inclinação suave de 15° para a esquerda para drenar o óleo retido nas galerias.",
      },
      {
        time: "03:10",
        seconds: 190,
        step: "Tampa da Tela (17 mm)",
        detail: "Desaperto da tampa sextavada M30; alívio suave da mola cônica interna.",
      },
      {
        time: "03:45",
        seconds: 225,
        step: "Limpeza da Tela Metálica",
        detail: "Remoção da tela 15421-KPL-900, inspeção de partículas e desengraxante a seco.",
      },
      {
        time: "04:30",
        seconds: 270,
        step: "Montagem com O-Ring Novo",
        detail: "Lábio de borracha voltado para dentro; O-ring lubrificado e torque de 20 N.m.",
      },
      {
        time: "05:10",
        seconds: 310,
        step: "Arruela 12 mm e Torque Cárter",
        detail: "Instalação da arruela 94109-12000 e aperto do bujão a 24 N.m (2,4 kgf.m).",
      },
      {
        time: "06:00",
        seconds: 360,
        step: "Abastecimento de Óleo",
        detail: "Carga de exatamente 800 ml de óleo Pro Honda 10W-30 JASO MB.",
      },
      {
        time: "07:15",
        seconds: 435,
        step: "Aferição na Vareta",
        detail: "Inserção da haste plástica SEM ROSQUEAR; nível entre as marcas hachuradas.",
      },
      {
        time: "08:00",
        seconds: 480,
        step: "Reset de Luz OIL CHANGE",
        detail: "Manter botão SET pressionado ao ligar a chave até o indicador apagar no LCD.",
      },
    ],
  },
  {
    id: "v_redutor",
    title: "Troca do Óleo da Transmissão Final (Redutor da Roda Traseira)",
    channel: "Mitch's Scooter Stuff",
    youtubeVideoId: "N4N1cQS3PbI",
    url: "https://www.youtube.com/watch?v=N4N1cQS3PbI",
    mirrorUrl: "https://www.youtube.com/watch?v=N4N1cQS3PbI",
    subsystem: "cvt",
    subsystemLabel: "Transmissão CVT V-Matic",
    description:
      "Substituição dos 120 ml de óleo da caixa redutora de engrenagens helicoidais com seringa dosadora, arruelas novas de alumínio 8 mm e torque de 13 N.m nos bujões.",
    difficulty: "Iniciante",
    estimatedDurationMin: 6,
    relatedPartNumbers: ["90131-896-650", "90495-MN5-000", "08232-M99-K1JT1"],
    torqueSpecs: [
      {
        name: "Bujão Inferior de Dreno M8",
        nm: 13,
        kgfm: 1.3,
        socket: "Sextavado 12 mm",
        notes: "Arruela nova de alumínio 8 mm (90495-MN5-000)",
      },
      {
        name: "Bujão Superior de Nível M8",
        nm: 13,
        kgfm: 1.3,
        socket: "Sextavado 12 mm",
        notes: "Abastecer até a crista inferior da rosca fêmea",
      },
    ],
    specialTools: [
      "Soquete 12 mm",
      "Torquímetro de precisão 5-25 N.m",
      "Seringa dosadora de silicone 150 ml com mangueira flexível",
    ],
    criticalGotcha:
      "REGRA DE OURO: Sempre afrouxar o bujão superior de enchimento antes de abrir o dreno inferior. Aperto além de 13 N.m quebra a carcaça de alumínio da balança traseira.",
    timestamps: [
      {
        time: "00:00",
        seconds: 0,
        step: "Ferramental do Redutor",
        detail: "Soquete 12 mm, torquímetro 5-25 N.m, seringa e 2 arruelas novas de 8 mm.",
      },
      {
        time: "00:40",
        seconds: 40,
        step: "Mapeamento dos Bujões",
        detail: "Identificação do parafuso superior de nível e parafuso inferior de dreno.",
      },
      {
        time: "01:10",
        seconds: 70,
        step: "Abertura Superior Prévia",
        detail: "Remoção obrigatória do parafuso de abastecimento antes do parafuso de dreno.",
      },
      {
        time: "01:45",
        seconds: 105,
        step: "Drenagem dos 120 ml",
        detail: "Retirada do bujão inferior e coleta do óleo velho em bandeja limpa.",
      },
      {
        time: "02:30",
        seconds: 150,
        step: "Diagnóstico de Emulsão",
        detail: "Inspeção contra aspecto leitoso (contaminação por água através do respiro).",
      },
      {
        time: "03:15",
        seconds: 195,
        step: "Arruela 8 mm e Torque Dreno",
        detail: "Arruela 90495-MN5-000 e aperto torquimétrico a 13 N.m (1,3 kgf.m).",
      },
      {
        time: "04:15",
        seconds: 255,
        step: "Dosagem com Seringa",
        detail: "Aspiração de exatamente 120 ml de 10W-30 com mangueira de silicone.",
      },
      {
        time: "04:45",
        seconds: 285,
        step: "Abastecimento Suave",
        detail: "Injeção gradual até o fluido nivelar na borda inferior da rosca fêmea.",
      },
      {
        time: "05:30",
        seconds: 330,
        step: "Fechamento e Torque Superior",
        detail: "Arruela nova de 8 mm e aperto do parafuso superior a 13 N.m.",
      },
      {
        time: "06:00",
        seconds: 360,
        step: "Giro Manual de Roda",
        detail: "Sanitização com álcool e checagem de giro silencioso da roda traseira livre.",
      },
    ],
  },
  {
    id: "v_filtro_ar",
    title: "Substituição do Filtro de Ar Viscoso Genuíno (NUNCA Soprar)",
    channel: "Mitch's Scooter Stuff",
    youtubeVideoId: "Q6sllA9xCz4",
    url: "https://www.youtube.com/watch?v=Q6sllA9xCz4",
    mirrorUrl: "https://www.youtube.com/watch?v=Q6sllA9xCz4",
    subsystem: "motor",
    subsystemLabel: "Motor eSP 149,3 cm³",
    description:
      "Substituição do elemento viscoso genuíno 17210-K0W-B00, esgotamento do condensado no tubo dreno e alerta mandatório de proibição de ar comprimido.",
    difficulty: "Iniciante",
    estimatedDurationMin: 4,
    relatedPartNumbers: ["17210-K0W-B00"],
    torqueSpecs: [
      {
        name: "Parafusos Autorrosqueantes da Tampa da Caixa de Ar",
        nm: 1.5,
        kgfm: 0.15,
        socket: "Chave JIS #2",
        notes: "Aperto cruzado manual moderado",
      },
    ],
    specialTools: [
      "Chave cruciforme padrão japonês JIS B 1012 #2",
      "Pano de microfibra",
      "Álcool isopropílico 99%",
    ],
    criticalGotcha:
      "PROIBIÇÃO ABSOLUTA DE BATER AR COMPRIMIDO NO ELEMENTO FILTRANTE. O filtro original é impregnado com óleo viscoso tackifier. O jato de ar dispersa o óleo, abre os microporos da celulose e permite a passagem de sílica para o cilindro.",
    timestamps: [
      {
        time: "00:00",
        seconds: 0,
        step: "Ferramentas & Peça Nova",
        detail: "Chave Phillips JIS #2, pano de limpeza e filtro viscoso 17210-K0W-B00.",
      },
      {
        time: "00:30",
        seconds: 30,
        step: "Remoção da Tampa Plástica",
        detail: "Soltura dos parafusos autorrosqueantes da carcaça sem espanar os apoios.",
      },
      {
        time: "01:05",
        seconds: 65,
        step: "Extração do Filtro Saturado",
        detail: "Retirada do elemento de celulose e inspeção de contaminação por poeira.",
      },
      {
        time: "01:45",
        seconds: 105,
        step: "Alerta: Proibição de Ar Comprimido",
        detail: "Demonstração de como o ar destrói o óleo viscoso e rasga as microfibras.",
      },
      {
        time: "02:15",
        seconds: 135,
        step: "Higienização da Caixa de Ar",
        detail: "Limpeza interna da caixa plástica com pano umedecido em álcool isopropílico.",
      },
      {
        time: "02:50",
        seconds: 170,
        step: "Encaixe e Aperto JIS",
        detail: "Assentamento perimetral do lábio de borracha e aperto cruzado uniforme.",
      },
    ],
  },
  {
    id: "v_cvt_primario",
    title: "Desmontagem do Variador Primário, Roletes 21g & Correia Bando",
    channel: "Mitch's Scooter Stuff",
    youtubeVideoId: "Zr5Rinr7Rzs",
    url: "https://www.youtube.com/watch?v=Zr5Rinr7Rzs",
    mirrorUrl: "https://www.youtube.com/watch?v=Zr5Rinr7Rzs",
    subsystem: "cvt",
    subsystemLabel: "Transmissão CVT V-Matic",
    description:
      "Abertura da tampa do CVT com gabarito de parafusos M6 (35 e 40 mm), chave em Y 07725-0030000, medição centesimal de roletes a seco e protocolo de inspeção da correia Kevlar.",
    difficulty: "Intermediário",
    estimatedDurationMin: 10,
    relatedPartNumbers: [
      "23100-K97-T01",
      "22123-K0W-B00",
      "22110-K0W-B00",
      "22105-K97-T00",
      "22011-K97-T00",
      "22102-K97-T01",
      "90441-K97-T00",
      "90201-415-000",
    ],
    torqueSpecs: [
      {
        name: "Porca M14 do Virabrequim (Variador Primário)",
        nm: 59,
        kgfm: 6.0,
        socket: "Soquete 22 mm",
        notes: "Trava em Y nos furos reforçados; arruela Belleville com OUTSIDE para fora",
      },
      {
        name: "Parafusos M6 da Tampa do CVT",
        nm: 10,
        kgfm: 1.0,
        socket: "Sextavado 8 mm",
        notes: "Aperto cruzado uniforme em 2 etapas",
      },
    ],
    specialTools: [
      "Chave T 8 mm longa",
      "Soquete sextavado 22 mm",
      "Trava Universal em Y 07725-0030000",
      "Torquímetro 20-100 N.m",
      "Paquímetro digital centesimal (DIN 862)",
      "Martelo de borracha",
    ],
    criticalGotcha:
      "PROIBIÇÃO ABSOLUTA DE GRAXA NOS ROLETES OU PISTAS DO VARIADOR. Os roletes trabalham 100% a seco. Graxa acumula pó de borracha, trava a subida centrífuga e desbalanceia a rampa. Nunca travar a chave nas aletas de ventilação.",
    timestamps: [
      {
        time: "00:00",
        seconds: 0,
        step: "Ferramental do Variador",
        detail: "Chave T 8 mm, soquete 22 mm, chave em Y 07725-0030000 e paquímetro.",
      },
      {
        time: "00:50",
        seconds: 50,
        step: "Duto Frontal & Espuma",
        detail: "Retirada da capa plástica protetora e duto de ventilação do CVT.",
      },
      {
        time: "01:50",
        seconds: 110,
        step: "Gabarito de Parafusos M6",
        detail: "Soltura cruzada 8 mm e separação dos comprimentos de 35 e 40 mm.",
      },
      {
        time: "02:40",
        seconds: 160,
        step: "Descolamento sem Alavancas",
        detail: "Batidas secas com martelo de borracha nos reforços da carcaça de alumínio.",
      },
      {
        time: "03:20",
        seconds: 200,
        step: "Pinos Guia e Junta",
        detail: "Recolhimento dos 2 pinos cilíndricos de aço e inspeção visual da junta.",
      },
      {
        time: "04:10",
        seconds: 250,
        step: "Instalação da Chave em Y",
        detail: "Encaixe dos pinos nos furos usinados da polia fixa sem tocar nas aletas.",
      },
      {
        time: "04:55",
        seconds: 295,
        step: "Porca M14 da Primária (22 mm)",
        detail: "Desaperto anti-horário e retirada da arruela Belleville (marca OUTSIDE).",
      },
      {
        time: "05:40",
        seconds: 340,
        step: "Extração do Bloco Variador",
        detail: "Retirada da face móvel segurando a placa de rampa traseira com os polegares.",
      },
      {
        time: "06:30",
        seconds: 390,
        step: "Medição dos Roletes (21g)",
        detail: "Aferição com paquímetro digital; descarte se < 19,5 mm ou facetados.",
      },
      {
        time: "07:15",
        seconds: 435,
        step: "Limpeza 100% a Seco",
        detail: "Higienização das canaletas com álcool isopropílico; proibição de graxa.",
      },
      {
        time: "08:00",
        seconds: 480,
        step: "Inspeção dos 3 Cursores Guia",
        detail: "Teste de folga na rampa plástica contra ruído de chocalho na marcha lenta.",
      },
      {
        time: "08:50",
        seconds: 530,
        step: "Metrologia da Correia Bando",
        detail: "Largura da correia (limite 18,5 mm) e inspeção de trincas internas na base.",
      },
    ],
  },
  {
    id: "v_cvt_secundario",
    title: "Desmontagem da Secundária, Compressor de Mola & Protocolo de Afundamento",
    channel: "Mitch's Scooter Stuff",
    youtubeVideoId: "Zr5Rinr7Rzs",
    url: "https://www.youtube.com/watch?v=Zr5Rinr7Rzs",
    mirrorUrl: "https://www.youtube.com/watch?v=Zr5Rinr7Rzs",
    subsystem: "cvt",
    subsystemLabel: "Transmissão CVT V-Matic",
    description:
      "Compressor 07LME-GZ40201 contra pré-carga balística de 50 kgf, soquete plano 39 mm (54 N.m), graxa poliureia MoS2 nas canaletas e o protocolo vital de afundamento da correia (Belt Sinking).",
    difficulty: "Crítico",
    estimatedDurationMin: 12,
    relatedPartNumbers: [
      "22100-K0W-N00",
      "90202-KN7-670",
      "22535-K0W-N00",
      "23233-K97-T00",
      "90202-187-000",
      "23225-KS4-000",
      "23100-K97-T01",
    ],
    torqueSpecs: [
      {
        name: "Porca Central M28 da Sapata de Embreagem (39 mm)",
        nm: 54,
        kgfm: 5.5,
        socket: "Soquete plano 39 mm",
        notes: "Montagem obrigatória sob compressão na prensa",
      },
      {
        name: "Porca M12 da Campana de Embreagem (19 mm)",
        nm: 49,
        kgfm: 5.0,
        socket: "Soquete 19 mm",
        notes: "Travar a campana com fixador de pinos",
      },
      {
        name: "Porca M14 do Variador Primário (22 mm)",
        nm: 59,
        kgfm: 6.0,
        socket: "Soquete 22 mm",
        notes: "Apertar somente após a correia estar afundada na secundária",
      },
    ],
    specialTools: [
      "Compressor de mola da embreagem 07LME-GZ40201",
      "Soquete plano sem chanfro 39 mm 07GMA-KS40100",
      "Fixador universal de pinos",
      "Soquete 19 mm",
      "Torquímetro 20-100 N.m",
      "Graxa de poliureia MoS2 para alta temperatura",
    ],
    criticalGotcha:
      "O ERRO MAIS CARO DA HONDA ADV 150: O SEGREDO DO BELT SINKING. Ao recolocar a correia, comprima com as mãos as polias secundárias e afunde a correia até o fundo da garganta. Deixe a frente 100% frouxa. Se apertar a porca M14 com a correia mordida na borda, cria-se falso torque; na primeira acelerada a correia desce, a polia afrouxa e o virabrequim é destruído.",
    timestamps: [
      {
        time: "09:00",
        seconds: 540,
        step: "Ferramentas da Secundária",
        detail: "Fixador de pinos, soquete 19 mm, compressor 07LME-GZ40201 e chave 39 mm.",
      },
      {
        time: "09:50",
        seconds: 590,
        step: "Remoção da Campana (19 mm)",
        detail: "Travamento da campana e desaperto da porca M12; diâmetro máx 125,5 mm.",
      },
      {
        time: "10:45",
        seconds: 645,
        step: "Inspeção das 3 Sapatas",
        detail: "Espessura da lona (limite 2,0 mm) e desvitrificação com lixa d'água 220.",
      },
      {
        time: "11:30",
        seconds: 690,
        step: "Instalação no Compressor",
        detail: "Anulação segura da pré-carga de 35 a 50 kgf contra risco balístico.",
      },
      {
        time: "12:15",
        seconds: 735,
        step: "Porca Central de 39 mm",
        detail: "Soltura da porca M28 com soquete sem chanfro 07GMA-KS40100.",
      },
      {
        time: "13:00",
        seconds: 780,
        step: "Desmontagem do Torque Cam",
        detail: "Retirada dos 3 pinos guia e roletes; inspeção de calos nas canaletas.",
      },
      {
        time: "13:45",
        seconds: 825,
        step: "Engraxamento com Poliureia",
        detail: "Aplicação de graxa de alta velocidade com MoS2 (> 260°C); remoção do excesso.",
      },
      {
        time: "14:30",
        seconds: 870,
        step: "O Segredo do Afundamento",
        detail: "Abertura manual das polias traseiras para afundar a correia dentada.",
      },
      {
        time: "15:15",
        seconds: 915,
        step: "Montagem sem Esmagamento",
        detail: "Posicionamento da polia fixa dianteira com a correia totalmente frouxa.",
      },
      {
        time: "16:00",
        seconds: 960,
        step: "Torques Finais com Torquímetro",
        detail: "Porca primária a 59 N.m (chave Y) e porca da campana a 49 N.m (pinos).",
      },
    ],
  },
  {
    id: "v_coolant",
    title: "Troca do Líquido de Arrefecimento & Sangria de Bolhas no Radiador",
    channel: "Mitch's Scooter Stuff",
    youtubeVideoId: "e-g8Wl14uUY",
    url: "https://www.youtube.com/watch?v=e-g8Wl14uUY",
    mirrorUrl: "https://www.youtube.com/watch?v=e-g8Wl14uUY",
    subsystem: "motor",
    subsystemLabel: "Motor eSP 149,3 cm³",
    description:
      "Dreno na bomba d'água (10 N.m com arruela de cobre 6,5 mm), Pro Coolant Tipo 2 OAT 50/50 e ciclo de 5 minutos de sangria ativa de ar com tampa aberta.",
    difficulty: "Intermediário",
    estimatedDurationMin: 11,
    relatedPartNumbers: [
      "19010-K0W-N01",
      "19037-K0W-N01",
      "19200-K97-T00",
      "90004-GHB-600",
      "90441-ME9-000",
      "08CLA-G01-020",
    ],
    torqueSpecs: [
      {
        name: "Parafuso de Dreno da Bomba d'Água M6",
        nm: 10,
        kgfm: 1.0,
        socket: "Sextavado 8 mm",
        notes: "Substituir arruela de cobre 6,5 mm (90441-ME9-000)",
      },
      {
        name: "Parafusos da Capa do Radiador",
        nm: 9,
        kgfm: 0.9,
        socket: "Sextavado 8 mm",
      },
    ],
    specialTools: [
      "Soquete 8 mm",
      "Torquímetro 5-25 N.m",
      "Seringa dosadora de sucção",
      "Bandeja coletora",
    ],
    criticalGotcha:
      "SANGRIA DE AR OBRIGATÓRIA COM TAMPA ABERTA. Ao abastecer, ligue o motor em marcha lenta por 3 a 5 minutos sem a tampa pressurizada. Dê 2 acelerações suaves a 2.500 rpm para expelir bolsas de ar presas no cabeçote. Rodar com ar no sistema superaquece o motor e queima a junta de cabeçote.",
    timestamps: [
      {
        time: "00:00",
        seconds: 0,
        step: "Ferramental & Insumos",
        detail: "Soquete 8 mm, torquímetro, Pro Coolant Tipo 2 e arruela de cobre 6,5 mm.",
      },
      {
        time: "00:45",
        seconds: 45,
        step: "Motor 100% Frio",
        detail: "Apoio no cavalete central; motor frio para não queimar e não trincar o bloco.",
      },
      {
        time: "01:25",
        seconds: 85,
        step: "Capa Plástica do Radiador",
        detail: "Desaperto dos parafusos externos e separação cuidadosa das travas plásticas.",
      },
      {
        time: "02:05",
        seconds: 125,
        step: "Abertura em Dois Estágios",
        detail: "Alívio de pressão com pano de proteção e remoção da tampa do radiador.",
      },
      {
        time: "02:50",
        seconds: 170,
        step: "Posicionamento da Bacia",
        detail: "Ajuste do recipiente sob a projeção do bujão inferior da bomba d'água.",
      },
      {
        time: "03:30",
        seconds: 210,
        step: "Remoção do Dreno M6 (8 mm)",
        detail: "Abertura do parafuso inferior e esgotamento do líquido velho em arco.",
      },
      {
        time: "04:20",
        seconds: 260,
        step: "Esgotamento do Expansor",
        detail: "Aspiração do fluido velho do reservatório com seringa de dosagem.",
      },
      {
        time: "05:10",
        seconds: 310,
        step: "Arruela Cobre e Torque Dreno",
        detail: "Arruela OEM 90441-ME9-000 e aperto do dreno a 10 N.m (1,0 kgf.m).",
      },
      {
        time: "06:00",
        seconds: 360,
        step: "Abastecimento pelo Gargalo",
        detail: "Adição lenta de Honda Pro Coolant Tipo 2 até o gargalo do radiador.",
      },
      {
        time: "06:50",
        seconds: 410,
        step: "Carga do Reservatório",
        detail: "Abastecimento do vaso de expansão até a marca superior UPPER.",
      },
      {
        time: "07:40",
        seconds: 460,
        step: "Partida com Tampa Aberta",
        detail: "Motor em lenta por 3-5 min; observação da subida contínua de bolhas.",
      },
      {
        time: "08:35",
        seconds: 515,
        step: "Acelerações Suaves de Sangria",
        detail: "2 a 3 giros suaves a 2.500 rpm para expelir bolsas de ar do cabeçote.",
      },
    ],
  },
  {
    id: "v_pastilhas",
    title: "Troca das Pastilhas de Freio Dianteiras Nissin & Recuo de Pistões",
    channel: "Mitch's Scooter Stuff",
    youtubeVideoId: "Yq0-AYya2yA",
    url: "https://www.youtube.com/watch?v=Yq0-AYya2yA",
    mirrorUrl: "https://www.youtube.com/watch?v=Yq0-AYya2yA",
    subsystem: "freios",
    subsystemLabel: "Freios Nissin & ABS",
    description:
      "Substituição das pastilhas sinterizadas Nissin, limpeza mandatória dos pistões com isopropanol, graxa de silicone Shin-Etsu nas buchas e torques oficiais dos cálipers.",
    difficulty: "Intermediário",
    estimatedDurationMin: 7,
    relatedPartNumbers: ["45251-K0W-N01", "06455-K0W-N01", "45150-K0W-N01"],
    torqueSpecs: [
      {
        name: "Parafusos de Fixação do Cáliper Dianteiro Nissin",
        nm: 30,
        kgfm: 3.1,
        socket: "Soquete 12 mm",
        notes: "Aplicar trava química média azul",
      },
      {
        name: "Pino Allen de Retenção das Pastilhas",
        nm: 17,
        kgfm: 1.7,
        socket: "Chave Allen 5 mm",
      },
      {
        name: "Plugue Tampão de Fenda do Pino",
        nm: 2.5,
        kgfm: 0.25,
        socket: "Fenda Plana",
      },
    ],
    specialTools: [
      "Soquete 12 mm",
      "Chave Allen 5 mm",
      "Chave fenda larga plana",
      "Torquímetro 5-50 N.m",
      "Álcool isopropílico e escova macia",
      "Graxa de silicone pura Shin-Etsu G-40M",
    ],
    criticalGotcha:
      "NUNCA EMPURRAR OS PISTÕES SUJOS PARA DENTRO DO CÁLIPER. A fuligem acumulada na lateral dos êmbolos rasga os retentores de poeira e trava o retorno do cáliper. Escove os pistões com álcool isopropílico antes do recuo. Bombeie a manete 5 vezes antes de rodar.",
    timestamps: [
      {
        time: "00:00",
        seconds: 0,
        step: "Ferramental de Freios",
        detail: "Soquetes 12 mm, Allen 5 mm, torquímetro, pastilhas OEM e álcool isopropílico.",
      },
      {
        time: "00:40",
        seconds: 40,
        step: "Alívio do Pino de Pastilha",
        detail: "Retirada do tampão de fenda plana e quebra do torque do pino Allen 5 mm.",
      },
      {
        time: "01:20",
        seconds: 80,
        step: "Remoção do Cáliper Dianteiro",
        detail: "Desaperto dos 2 parafusos M10 com soquete 12 mm e desencaixe do garfo.",
      },
      {
        time: "02:00",
        seconds: 120,
        step: "Extração das Pastilhas",
        detail: "Retirada do pino, mola de inox e pastilhas gastas (limite 1,0 mm).",
      },
      {
        time: "02:45",
        seconds: 165,
        step: "Limpeza Mandatória dos Pistões",
        detail: "Escovação dos êmbolos cromados com isopropanol antes de recuar.",
      },
      {
        time: "03:30",
        seconds: 210,
        step: "Graxa de Silicone nos Pinos",
        detail: "Aplicação de silicone puro Shin-Etsu nas buchas de borracha EPDM.",
      },
      {
        time: "04:15",
        seconds: 255,
        step: "Montagem das Novas Pastilhas",
        detail: "Encaixe das placas 06455-K0W-N01 e pino com mola antivibração.",
      },
      {
        time: "05:05",
        seconds: 305,
        step: "Torques dos Cálipers e Pinos",
        detail: "Parafusos do cáliper a 30 N.m (trava azul) e pino Allen a 17 N.m.",
      },
      {
        time: "06:30",
        seconds: 390,
        step: "Bombeamento do Manete",
        detail: "4 a 6 acionamentos firmes do manete para encostar as pastilhas no disco.",
      },
    ],
  },
  {
    id: "v_pastilhas_tras",
    title: "Manutenção do Freio Traseiro Nissin & Substituição das Pastilhas",
    channel: "Mitch's Scooter Stuff",
    youtubeVideoId: "2R1afTOFd6Q",
    url: "https://www.youtube.com/watch?v=2R1afTOFd6Q",
    mirrorUrl: "https://www.youtube.com/watch?v=2R1afTOFd6Q",
    subsystem: "freios",
    subsystemLabel: "Freios Nissin & ABS",
    description:
      "Substituição das pastilhas de freio traseiras Nissin 06435-K0W-N01, limpeza do cáliper deslizante de 1 pistão e torque oficial a 22 N.m.",
    difficulty: "Intermediário",
    estimatedDurationMin: 6,
    relatedPartNumbers: ["43251-K0W-N01", "06435-K0W-N01", "43150-K0W-N01"],
    torqueSpecs: [
      {
        name: "Parafusos de Fixação do Cáliper Traseiro",
        nm: 22,
        kgfm: 2.2,
        socket: "Soquete 12 mm",
      },
      {
        name: "Pino Allen de Retenção Traseiro",
        nm: 17,
        kgfm: 1.7,
        socket: "Chave Allen 5 mm",
      },
    ],
    specialTools: [
      "Soquete 12 mm",
      "Chave Allen 5 mm",
      "Torquímetro 5-30 N.m",
      "Álcool isopropílico",
    ],
    criticalGotcha:
      "INSPEÇÃO DE ESPESSURA MÍNIMA: Substituir quando a espessura da lona atingir 1,0 mm (ou ranhura indicadora sumir). Disco traseiro wave com espessura mínima de 3,5 mm.",
    timestamps: [
      {
        time: "00:00",
        seconds: 0,
        step: "Ferramental & Peças Traseiras",
        detail: "Soquetes 12 mm, Allen 5 mm, torquímetro e pastilhas 06435-K0W-N01.",
      },
      {
        time: "00:45",
        seconds: 45,
        step: "Desaperto do Cáliper Traseiro",
        detail: "Remoção dos parafusos de fixação no suporte da balança.",
      },
      {
        time: "01:30",
        seconds: 90,
        step: "Extração das Pastilhas Gastas",
        detail: "Retirada do pino de retenção e aferição da espessura residual.",
      },
      {
        time: "02:20",
        seconds: 140,
        step: "Limpeza do Pistão Traseiro",
        detail: "Higienização periférica com isopropanol antes do recuo do êmbolo.",
      },
      {
        time: "03:15",
        seconds: 195,
        step: "Instalação das Novas Pastilhas",
        detail: "Montagem com graxa de silicone nos pinos deslizantes.",
      },
      {
        time: "04:10",
        seconds: 250,
        step: "Torques de Aperto Traseiro",
        detail: "Parafusos a 22 N.m e pino a 17 N.m.",
      },
      {
        time: "05:00",
        seconds: 300,
        step: "Bombeamento do Manete Traseiro",
        detail: "Pressurização do circuito e confirmação de curso firme.",
      },
    ],
  },
  {
    id: "v_fluido_freio",
    title: "Substituição do Fluido DOT 4 com Blindagem do Modulador ABS",
    channel: "Mitch's Scooter Stuff",
    youtubeVideoId: "vIfn3ss9iyQ",
    url: "https://www.youtube.com/watch?v=vIfn3ss9iyQ",
    mirrorUrl: "https://www.youtube.com/watch?v=vIfn3ss9iyQ",
    subsystem: "freios",
    subsystemLabel: "Freios Nissin & ABS",
    description:
      "Chave JIS #2 na tampa (1,5 N.m), sangria contínua sem esvaziar o reservatório (blindagem contra ar no modulador ABS) e torque da válvula de purga a 5,4 N.m.",
    difficulty: "Crítico",
    estimatedDurationMin: 10,
    relatedPartNumbers: ["57110-K0W-N01", "45150-K0W-N01", "08203-0004"],
    torqueSpecs: [
      {
        name: "Válvula de Purga / Sangria no Cáliper",
        nm: 5.4,
        kgfm: 0.55,
        socket: "Chave de estria 8 mm",
        notes: "Torquímetro de precisão baixa escala",
      },
      {
        name: "Parafusos da Tampa do Reservatório Mestre",
        nm: 1.5,
        kgfm: 0.15,
        socket: "Chave JIS #2",
        notes: "Uso obrigatório de chave JIS sem fundo cônico",
      },
    ],
    specialTools: [
      "Chave cruciforme JIS B 1012 #2",
      "Chave de estria 8 mm",
      "Mangueira de vinil transparente 3/16 polegadas",
      "Seringa dosadora de sucção",
      "Torquímetro de escala baixa (1-10 N.m)",
    ],
    criticalGotcha:
      "NUNCA DEIXE ENTRAR AR NO MODULADOR ABS NISSIN. Ao sangrar, retire no máximo 70% do fluido velho do reservatório; mantenha o orifício do fundo coberto e reabasteça com DOT 4 novo imediatamente. A entrada de bolhas no módulo ABS exige equipamento de pulso elétrico para purga.",
    timestamps: [
      {
        time: "00:00",
        seconds: 0,
        step: "Ferramental & DOT 4 Novo",
        detail: 'Chave JIS #2, chave estria 8 mm, mangueira vinil 3/16", seringa e DOT 4.',
      },
      {
        time: "00:50",
        seconds: 50,
        step: "Proteção das Carenagens",
        detail: "Cobertura total com toalhas contra respingos corrosivos de fluido de freio.",
      },
      {
        time: "01:35",
        seconds: 95,
        step: "Abertura com Chave JIS #2",
        detail: "Remoção dos 2 parafusos do reservatório mestre com JIS sem espanar.",
      },
      {
        time: "02:20",
        seconds: 140,
        step: "Aspiração & Regra dos 30%",
        detail: "Retirada de 70% do fluido velho sem nunca descobrir o fundo da galeria.",
      },
      {
        time: "03:00",
        seconds: 180,
        step: "Carga com DOT 4 Novo",
        detail: "Abastecimento imediato até a borda superior do reservatório com fluido claro.",
      },
      {
        time: "03:45",
        seconds: 225,
        step: "Conexão da Mangueira",
        detail: "Chave 8 mm e mangueira transparente no sangrador do cáliper.",
      },
      {
        time: "04:30",
        seconds: 270,
        step: "Ciclo em 4 Tempos de Sangria",
        detail: "Apertar alavanca -> abrir sangrador -> fechar sangrador -> soltar devagar.",
      },
      {
        time: "05:40",
        seconds: 340,
        step: "Monitoramento Contínuo",
        detail: "Reposição de DOT 4 a cada 3 ciclos para blindar o modulador ABS contra ar.",
      },
      {
        time: "06:40",
        seconds: 400,
        step: "Critério de Fluido Límpido",
        detail: "Continuidade dos ciclos até emergir fluido 100% transparente sem bolhas.",
      },
      {
        time: "07:25",
        seconds: 445,
        step: "Torque na Válvula de Purga",
        detail: "Aperto final do parafuso sangrador rigorosamente a 5,4 N.m.",
      },
      {
        time: "08:10",
        seconds: 490,
        step: "Montagem da Tampa JIS",
        detail: "Instalação do diafragma EPDM e aperto dos parafusos JIS a 1,5 N.m.",
      },
      {
        time: "08:50",
        seconds: 530,
        step: "Neutralização com Água",
        detail: "Lavagem completa de cálipers e guidão com água para neutralizar resíduos.",
      },
    ],
  },
  {
    id: "v_showa_shocks",
    title: "Substituição e Manutenção dos Amortecedores Showa Piggyback",
    channel: "Mitch's Scooter Stuff",
    youtubeVideoId: "Wvuw_ycR-SM",
    url: "https://www.youtube.com/watch?v=Wvuw_ycR-SM",
    mirrorUrl: "https://www.youtube.com/watch?v=Wvuw_ycR-SM",
    subsystem: "suspensao",
    subsystemLabel: "Ciclística & Suspensão Showa",
    description:
      "Remoção segura, alinhamento do reservatório dourado piggyback, buchas vulcanizadas e torques oficiais: parafuso superior M10 a 40 N.m e parafuso inferior M8 a 27 N.m.",
    difficulty: "Intermediário",
    estimatedDurationMin: 7,
    relatedPartNumbers: ["52400-K0W-N01", "52500-K0W-N01"],
    torqueSpecs: [
      {
        name: "Parafuso Superior M10 do Amortecedor (Chassi)",
        nm: 40,
        kgfm: 4.1,
        socket: "Sextavado 14 mm",
      },
      {
        name: "Parafuso Inferior M8 do Amortecedor (Balança)",
        nm: 27,
        kgfm: 2.8,
        socket: "Sextavado 12 mm",
      },
    ],
    specialTools: [
      "Soquetes 12 e 14 mm (encaixe 3/8 pol)",
      "Extensão média",
      "Torquímetro de precisão 20-60 N.m",
      "Calço de madeira para a roda traseira",
    ],
    criticalGotcha:
      "APOIO RÍGIDO SOB O PNEU TRASEIRO. Com a scooter no cavalete central, calce um bloco de madeira sob o pneu antes de soltar os parafusos. Isso impede que a balança despenque e danifique o flexível de freio ou chicote.",
    timestamps: [
      {
        time: "00:00",
        seconds: 0,
        step: "Ferramental Showa",
        detail: 'Soquetes 12 e 14 mm (3/8"), extensões e torquímetro de precisão.',
      },
      {
        time: "00:40",
        seconds: 40,
        step: "Suporte da Roda Traseira",
        detail: "Apoio no cavalete central com calço sob a roda para aliviar o peso.",
      },
      {
        time: "01:20",
        seconds: 80,
        step: "Parafuso Inferior M8 (12 mm)",
        detail: "Soltura do parafuso inferior na ancoragem da balança de alumínio.",
      },
      {
        time: "02:00",
        seconds: 120,
        step: "Parafuso Superior M10 (14 mm)",
        detail: "Desaperto do parafuso superior no chassi e retirada do amortecedor.",
      },
      {
        time: "02:50",
        seconds: 170,
        step: "Inspeção de Buchas de Borracha",
        detail: "Checagem contra folgas radiais e ressecamento elástico das buchas.",
      },
      {
        time: "03:40",
        seconds: 220,
        step: "Instalação do Conjunto Showa",
        detail: "Alinhamento do reservatório dourado e rosca manual dos parafusos.",
      },
      {
        time: "04:30",
        seconds: 270,
        step: "Torques Oficiais Showa",
        detail: "Parafuso superior M10 a 40 N.m e parafuso inferior M8 a 27 N.m.",
      },
      {
        time: "05:15",
        seconds: 315,
        step: "Alinhamento & Teste Dinâmico",
        detail: "Conferência de folga simétrica e teste estático de retorno das molas.",
      },
    ],
  },
  {
    id: "v_carenagens",
    title: "Remoção de Carenagens, Painéis do Assoalho & Presilhas Push-Pins",
    channel: "Mitch's Scooter Stuff",
    youtubeVideoId: "uCQRzi4LAfs",
    url: "https://www.youtube.com/watch?v=uCQRzi4LAfs",
    mirrorUrl: "https://www.youtube.com/watch?v=uCQRzi4LAfs",
    subsystem: "chassi",
    subsystemLabel: "Chassi, Baú & Carenagens",
    description:
      "Mecânica das presilhas push-pins (recuo central de 2 mm), tapetes do assoalho, travas snap-fit sem quebras e desobstrução do baú de 27 L para acesso ao motor.",
    difficulty: "Iniciante",
    estimatedDurationMin: 6,
    relatedPartNumbers: ["90683-GAZ-003", "81250-K0W-N00", "50100-K0W-N00"],
    torqueSpecs: [
      {
        name: "Parafusos do Baú de Carga M6 (10 mm)",
        nm: 10,
        kgfm: 1.0,
        socket: "Sextavado 10 mm",
      },
    ],
    specialTools: [
      "Chave Phillips JIS #2",
      "Espátulas de nylon automotivas para forração",
      "Soquete 10 mm",
    ],
    criticalGotcha:
      "COMO DESTRAVAR PUSH-PINS SEM QUEBRAR: Empurre o pino central para dentro apenas 2 mm (ouvirá um clique leve). Puxe o corpo da presilha pela borda externa. Se empurrar fundo demais, o pino cai no túnel do chassi.",
    timestamps: [
      {
        time: "00:00",
        seconds: 0,
        step: "Ferramental de Carenagens",
        detail: "Chave JIS #2, espátula de nylon automotiva e recipientes para parafusos.",
      },
      {
        time: "00:45",
        seconds: 45,
        step: "Mecânica dos Push-Pins",
        detail: "Recuo de 2 mm no pino central do push-pin para destravar sem quebrar.",
      },
      {
        time: "01:30",
        seconds: 90,
        step: "Painéis e Tapetes do Assoalho",
        detail: "Desencaixe dos coxins de borracha do piso e parafusos de 10 mm.",
      },
      {
        time: "02:30",
        seconds: 150,
        step: "Desacoplamento das Travas Internas",
        detail: "Deslizamento horizontal com espátula plástica para não romper garras snap-fit.",
      },
      {
        time: "03:45",
        seconds: 225,
        step: "Remoção do Baú de Carga (27 L)",
        detail: "Retirada dos 4 parafusos de 10 mm do fundo do baú e desconexão do cabo.",
      },
      {
        time: "05:00",
        seconds: 300,
        step: "Desobstrução do Topo do Cabeçote",
        detail: "Elevação do compartimento revelando o bloco eSP, bico injetor e chicote elétrico.",
      },
    ],
  },
  {
    id: "v_vela_ignicao",
    title: "Inspeção e Troca da Vela de Ignição Especial NGK MR8K-9",
    channel: "Mitch's Scooter Stuff",
    youtubeVideoId: "LGkAgp4MQXk",
    url: "https://www.youtube.com/watch?v=LGkAgp4MQXk",
    mirrorUrl: "https://www.youtube.com/watch?v=LGkAgp4MQXk",
    subsystem: "motor",
    subsystemLabel: "Motor eSP 149,3 cm³",
    description:
      "Acesso pelo túnel central, chave articulada 16 mm, aferição do gap de 0,80-0,90 mm, sopro preventivo da cavidade e aperto torquimétrico a 16 N.m.",
    difficulty: "Iniciante",
    estimatedDurationMin: 7,
    relatedPartNumbers: ["31918-K97-T01"],
    torqueSpecs: [
      {
        name: "Vela de Ignição Especial NGK MR8K-9 (Rosca M10)",
        nm: 16,
        kgfm: 1.6,
        socket: "Chave de vela 16 mm articulada",
      },
    ],
    specialTools: [
      "Chave articulada de vela 16 mm com retentor magnético/borracha",
      "Cálibre de lâminas centesimal",
      "Tubo de ar comprimido para sopro da rosca",
    ],
    criticalGotcha:
      "SOPRO PREVENTIVO DA CAVIDADE ANTES DE DESENROSCAR. A vela fica inclinada no túnel central e acumula areia no poço. Se soltar a vela sem aspirar/soprar antes, grãos de sílica caem direto dentro do cilindro, riscando camisa e anéis.",
    timestamps: [
      {
        time: "00:00",
        seconds: 0,
        step: "Acesso pelo Painel Lateral",
        detail: "Remoção da tampa plástica de inspeção no túnel central esquerdo.",
      },
      {
        time: "00:40",
        seconds: 40,
        step: "Desconexão do Cachimbo",
        detail:
          "Puxar firmemente pelo corpo rígido de borracha sem tracionar o cabo de alta tensão.",
      },
      {
        time: "01:20",
        seconds: 80,
        step: "Sopro da Cavidade da Vela",
        detail: "Limpeza de areia com ar comprimido antes de desenroscar a rosca M10.",
      },
      {
        time: "02:00",
        seconds: 120,
        step: "Chave de Vela Articulada 16 mm",
        detail: "Extração segura da vela NGK MR8K-9 com soquete de borracha protetora.",
      },
      {
        time: "02:45",
        seconds: 165,
        step: "Aferição do Gap (0,80 a 0,90 mm)",
        detail: "Aferição centesimal com cálibre de lâminas na faixa de fábrica e leitura de cor.",
      },
      {
        time: "03:30",
        seconds: 210,
        step: "Rosqueamento e Torque 16 N.m",
        detail: "Rosqueamento manual até o encosto e torque oficial a 16 N.m.",
      },
    ],
  },
  {
    id: "v_valvulas_esp",
    title: "Regulagem de Folga de Válvulas a Frio (< 35°C) no Bloco eSP",
    channel: "Mitch's Scooter Stuff",
    youtubeVideoId: "Rky-VGvqiy8",
    url: "https://www.youtube.com/watch?v=Rky-VGvqiy8",
    mirrorUrl: "https://www.youtube.com/watch?v=Rky-VGvqiy8",
    subsystem: "motor",
    subsystemLabel: "Motor eSP 149,3 cm³",
    description:
      "Alinhamento do PMS marcação 'T' no alternador, cálibre centesimal (Adm: 0,10 mm / Esc: 0,24 mm) e aperto da contraporca M5 a 10 N.m com chave quadrada de tucho.",
    difficulty: "Avançado",
    estimatedDurationMin: 11,
    relatedPartNumbers: [
      "12310-K0W-N00",
      "12391-K97-T00",
      "14711-K97-T00",
      "14721-K97-T00",
      "90206-001-000",
    ],
    torqueSpecs: [
      {
        name: "Contraporcas de Ajuste de Válvula M5",
        nm: 10,
        kgfm: 1.0,
        socket: "Chave de estria 9 mm",
        notes: "Imobilizar parafuso quadrado com ferramenta de tucho",
      },
      {
        name: "Parafusos da Tampa de Válvulas M6",
        nm: 10,
        kgfm: 1.0,
        socket: "Sextavado 10 mm",
      },
    ],
    specialTools: [
      "Cálibre de lâminas centesimal (lâminas 0,10 mm e 0,24 mm)",
      "Chave de estria 9 mm",
      "Ferramenta quadrada de ajuste de tucho 3 mm",
      "Soquete 17 mm para giro do alternador no sentido horário",
    ],
    criticalGotcha:
      "REGULAGEM EXCLUSIVAMENTE COM MOTOR FRIO (< 35°C). Se regular com motor morno, a dilatação do cabeçote falseia a leitura; ao esfriar, as válvulas ficam completamente enforcadas, gerando perda total de compressão matinal.",
    timestamps: [
      {
        time: "00:00",
        seconds: 0,
        step: "Ferramentas de Válvula",
        detail: "Cálibre centesimal, soquetes 10 e 17 mm, chave 9 mm e ferramenta de tucho.",
      },
      {
        time: "01:00",
        seconds: 60,
        step: "Remoção do Baú de Carga",
        detail: "Retirada dos 4 parafusos de 10 mm e presilhas para expor o cabeçote.",
      },
      {
        time: "02:10",
        seconds: 130,
        step: "Tampa do Cabeçote (10 mm)",
        detail: "Desconexão da mangueira do respiro e dos 2 parafusos de 10 mm.",
      },
      {
        time: "03:20",
        seconds: 200,
        step: "Giro do Alternador ao PMS",
        detail: "Soquete 17 mm horário; marca 'T' e traços paralelos no comando.",
      },
      {
        time: "04:30",
        seconds: 270,
        step: "Teste Tátil dos Balancins",
        detail: "Conferência de folga livre nos balancins roletados de adm e escape.",
      },
      {
        time: "05:30",
        seconds: 330,
        step: "Medição com Cálibre",
        detail: "Inserção com arrasto suave (Adm: 0,10 mm justa; 0,13 mm não entra).",
      },
      {
        time: "06:40",
        seconds: 400,
        step: "Ajuste com Chave 9 mm e Tucho",
        detail: "Imobilização do parafuso quadrado e aperto da contraporca a 10 N.m.",
      },
      {
        time: "07:40",
        seconds: 460,
        step: "Conferência Dupla Pós-Aperto",
        detail: "Teste passa/não passa com lâmina para comprovar ausência de desvio.",
      },
      {
        time: "08:45",
        seconds: 525,
        step: "Montagem da Tampa (10 N.m)",
        detail: "Junta de borracha limpa e aperto em cruz dos parafusos a 10 N.m.",
      },
    ],
  },
  {
    id: "v_valvula_enforcada",
    title: "Regulagem de Válvulas na Prática & Alerta de Válvula Enforcada",
    channel: "Magnetron Motos (Prof. Jackson Abreu)",
    youtubeVideoId: "rzKGsj9g90E",
    url: "https://www.youtube.com/watch?v=rzKGsj9g90E",
    mirrorUrl: "https://www.youtube.com/watch?v=rzKGsj9g90E",
    subsystem: "motor",
    subsystemLabel: "Motor eSP 149,3 cm³",
    description:
      "Evidência empírica de como regular válvulas com motor morno enforca a admissão, destrói a compressão do cilindro e impede a scooter de ligar no início da manhã.",
    difficulty: "Avançado",
    estimatedDurationMin: 8,
    relatedPartNumbers: ["14711-K97-T00", "14721-K97-T00", "90206-001-000"],
    torqueSpecs: [
      {
        name: "Contraporcas M5 de Regulagem",
        nm: 10,
        kgfm: 1.0,
        socket: "Chave de estria 9 mm",
      },
    ],
    specialTools: [
      "Manômetro de compressão de cilindro",
      "Cálibre de lâminas em leque",
      "Chave 9 mm",
    ],
    criticalGotcha:
      "SINTOMA CLÁSSICO DE VÁLVULA ENFORCADA: A moto roda perfeitamente o dia todo, mas na manhã seguinte o motor de partida gira livre e o motor não pega de jeito nenhum. Causa raiz: ausência de folga térmica a frio.",
    timestamps: [
      {
        time: "00:00",
        seconds: 0,
        step: "Sintomas do Defeito",
        detail: "Perda de compressão a frio e recusa do motor em ligar pela manhã.",
      },
      {
        time: "01:30",
        seconds: 90,
        step: "Mecanismo da Dilatação",
        detail:
          "Por que o coeficiente de expansão do cabeçote exige motor rigorosamente frio (< 35°C).",
      },
      {
        time: "03:00",
        seconds: 180,
        step: "Alinhamento Rigoroso no PMS",
        detail: "Marca T e balancins livres sem pressão no comando de válvulas.",
      },
      {
        time: "04:20",
        seconds: 260,
        step: "Uso do Cálibre com Arrasto",
        detail: "Sensibilidade tátil do cálibre de lâminas sem dobrar a fita metálica.",
      },
      {
        time: "05:40",
        seconds: 340,
        step: "Aperto da Contraporca M5",
        detail: "Imobilização firme com ferramenta de tucho para não alterar a folga.",
      },
      {
        time: "07:00",
        seconds: 420,
        step: "Teste de Partida e Lenta",
        detail: "Partida instantânea no toque do botão e marcha lenta cravada nos 1.700 rpm.",
      },
    ],
  },
  {
    id: "v_tbi_dlc",
    title: "Conector de Diagnóstico DLC Motos Honda & Jumper SCS",
    channel: "Palheta Motos",
    youtubeVideoId: "WjvK1Y95MW0",
    url: "https://www.youtube.com/watch?v=WjvK1Y95MW0",
    mirrorUrl: "https://www.youtube.com/watch?v=WjvK1Y95MW0",
    subsystem: "pgmfi",
    subsystemLabel: "Injeção PGM-FI & Eletrônica",
    description:
      "Localização dos terminais do conector DLC de 4 vias sob o assento, instalação do jumper nos fios Marrom e Verde/Preto e prevenção contra curto na linha de 12V.",
    difficulty: "Intermediário",
    estimatedDurationMin: 7,
    relatedPartNumbers: [
      "16400-K0W-N01",
      "16450-K0W-N01",
      "16430-GFM-902",
      "16060-KVS-J01",
      "070PZ-ZY30100",
      "38770-K0W-N01",
    ],
    torqueSpecs: [
      {
        name: "Parafusos do Coletor de Admissão M6",
        nm: 10,
        kgfm: 1.0,
        socket: "Sextavado 10 mm",
      },
      {
        name: "Parafusos da Válvula IACV",
        nm: 2.1,
        kgfm: 0.21,
        socket: "Chave JIS #2",
      },
    ],
    specialTools: [
      "Fio de jumper em U (ou Jumper SCS 070PZ-ZY30100)",
      "Álcool isopropílico 99%",
      "Cotonetes e pano de microfibra",
    ],
    criticalGotcha:
      "A PELÍCULA PRETA DE MoS2 NO CORPO KEIHIN NÃO É SUJEIRA! Trata-se de uma camada cerâmica de bissulfeto de molibdênio para vedação periférica da borboleta. Usar solventes agressivos (CAR 80) ou lixas remove essa película, gerando entrada falsa de ar e marcha lenta acelerada irreversível.",
    timestamps: [
      {
        time: "00:00",
        seconds: 0,
        step: "Localização do DLC Vermelho",
        detail: "Acesso ao conector de 4 vias sob a tampa do assento.",
      },
      {
        time: "01:10",
        seconds: 70,
        step: "Identificação dos Terminais",
        detail: "Terminal 1 Marrom (SCS) e Terminal 2 Verde/Preto (Terra).",
      },
      {
        time: "02:20",
        seconds: 140,
        step: "Blindagem Contra a Linha 12V",
        detail: "Alerta vital para não encostar nos pinos de alimentação da bateria.",
      },
      {
        time: "03:40",
        seconds: 220,
        step: "Ligar a Chave e Ler o Painel",
        detail: "Observação do comportamento da luz espia MIL no LCD.",
      },
      {
        time: "05:00",
        seconds: 300,
        step: "Ciclo de Reset em Menos de 5s",
        detail: "Desconexão e reconexão rápida para disparar o reset na ECM.",
      },
      {
        time: "06:20",
        seconds: 380,
        step: "Confirmação do Reset",
        detail: "Piscar acelerado contínuo indicando sucesso do protocolo.",
      },
    ],
  },
  {
    id: "v_mil_morse",
    title: "Como Apagar Defeitos Passados pelo Conector DLC & Decodificação da MIL",
    channel: "Prof. Jackson Abreu",
    youtubeVideoId: "udz3VbG_UzE",
    url: "https://www.youtube.com/watch?v=udz3VbG_UzE",
    mirrorUrl: "https://www.youtube.com/watch?v=udz3VbG_UzE",
    subsystem: "pgmfi",
    subsystemLabel: "Injeção PGM-FI & Eletrônica",
    description:
      "Leitura de falhas ativas e históricas gravadas no ECM: dezenas longas (1,3s) e unidades curtas (0,5s) no painel de instrumentos sem scanner proprietário.",
    difficulty: "Intermediário",
    estimatedDurationMin: 8,
    relatedPartNumbers: ["070PZ-ZY30100", "38770-K0W-N01", "37870-KZR-601", "16060-KVS-J01"],
    torqueSpecs: [],
    specialTools: ["Fio de cobre isolado em U ou Jumper SCS oficial Honda 070PZ-ZY30100"],
    criticalGotcha:
      "DIFERENÇA ENTRE FALHA ATIVA E HISTÓRICA: Se a MIL piscar logo que a chave for ligada (sem jumper), há falha crítica ativa. Com jumper inserido, piscadas contínuas revelam códigos armazenados na memória EEPROM que necessitam de reset.",
    timestamps: [
      {
        time: "00:00",
        seconds: 0,
        step: "Defeito Presente vs Defeito Passado",
        detail: "Como diferenciar falha em tempo real de registros históricos.",
      },
      {
        time: "01:15",
        seconds: 75,
        step: "Decodificação Morse da MIL",
        detail: "Dezenas longas (1,3 s) e unidades rápidas (0,5 s) no painel.",
      },
      {
        time: "02:30",
        seconds: 150,
        step: "Jumper SCS no Conector DLC",
        detail: "Conexão dos pinos 1 e 2 no conector vermelho de 4 vias.",
      },
      {
        time: "03:45",
        seconds: 225,
        step: "O Procedimento do Reset",
        detail: "Desconectar o jumper por 3 segundos e reconectar com a chave ligada.",
      },
      {
        time: "05:00",
        seconds: 300,
        step: "Confirmação Visual",
        detail: "Lâmpada MIL começa a piscar continuamente em frequência rápida.",
      },
      {
        time: "06:15",
        seconds: 375,
        step: "Remoção do Jumper e Teste Final",
        detail: "Desligar chave, retirar jumper e partida do motor sem erros.",
      },
    ],
  },
];

/**
 * Retorna um vídeo técnico oficial pelo ID
 */
export function getVideoById(id: string): TechnicalVideoItem | undefined {
  return ADV150_TECHNICAL_VIDEOS.find((v) => v.id === id);
}

/**
 * Filtra os vídeos técnicos pelo subsistema mecânico
 */
export function getVideosBySubsystem(subsystem: CatalogSubsystemId | "all"): TechnicalVideoItem[] {
  if (subsystem === "all") {
    return [...ADV150_TECHNICAL_VIDEOS];
  }
  return ADV150_TECHNICAL_VIDEOS.filter((v) => v.subsystem === subsystem);
}

/**
 * Busca vídeos por termo textual em título, canal, descrição ou passos
 */
export function searchVideos(query: string): TechnicalVideoItem[] {
  const normalized = query.toLowerCase().trim();
  if (!normalized) return [...ADV150_TECHNICAL_VIDEOS];

  return ADV150_TECHNICAL_VIDEOS.filter(
    (v) =>
      v.title.toLowerCase().includes(normalized) ||
      v.description.toLowerCase().includes(normalized) ||
      v.channel.toLowerCase().includes(normalized) ||
      v.subsystemLabel.toLowerCase().includes(normalized) ||
      v.relatedPartNumbers.some((p) => p.toLowerCase().includes(normalized)) ||
      v.timestamps.some(
        (t) =>
          t.step.toLowerCase().includes(normalized) || t.detail.toLowerCase().includes(normalized),
      ),
  );
}

/**
 * Monta URL de embed segura com youtube-nocookie e parâmetros de seek/autoplay
 */
export function buildEmbedUrl(youtubeVideoId: string, startSeconds = 0, autoplay = false): string {
  const base = `https://www.youtube-nocookie.com/embed/${youtubeVideoId}?enablejsapi=1&rel=0&modestbranding=1`;
  const startParam = startSeconds > 0 ? `&start=${startSeconds}` : "";
  const autoplayParam = autoplay ? "&autoplay=1" : "";
  return `${base}${startParam}${autoplayParam}`;
}
